import { useEffect, useState } from 'react';
import {
  Alert,
  AlertTitle,
  Box,
  Chip,
  CircularProgress,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { useNotify, useRecordContext, type RaRecord } from 'react-admin';
import { SectionCard } from '../shared/Panel';
import {
  fetchMessageContent,
  toErrorMessage,
  type MessageContent,
  type MessageStation,
} from './messageContentApi';

/**
 * Zawartość komunikatu VIA — stacje, przesyłki, pozycje (W11).
 *
 * ## Co było wcześniej
 *
 * Zrzut surowego JSON-a. Importujemy cały komunikat, ale wszystko, czego
 * operator potrzebuje, żeby sprawdzić zlecenie — adresy stacji, okna czasowe,
 * pozycje, ilości — było widoczne wyłącznie jako ściana `snake_case`.
 *
 * ## Dlaczego pobieramy od razu, a nie na przycisk
 *
 * Inaczej niż panele zapisu i podpowiedzi: tu nie ma żadnego skutku ubocznego
 * ani kosztu (jeden wiersz z bazy), a operator otworzył ten ekran właśnie po
 * to, żeby zobaczyć zawartość. Przycisk „pokaż" przed treścią, po którą się
 * przyszło, jest kliknięciem bez powodu.
 *
 * ## Surowy komunikat zostaje
 *
 * Nasza normalizacja czyta znane znaczniki. Format klienta jest otwarty, więc
 * podgląd surowego poddrzewa zostaje jako furtka na wypadek, gdy w komunikacie
 * będzie coś, czego jeszcze nie nazwaliśmy — tylko schowany, nie zamiast
 * tabel.
 */

const STATION_TYPE_LABELS: Record<string, string> = {
  loading: 'załadunek',
  unloading: 'rozładunek',
};

const formatTimeWindow = (day: string | null, time: string | null) => {
  if (!day && !time) return '—';
  return [day, time].filter(Boolean).join(' ');
};

const formatAddress = (s: MessageStation) => {
  const line = [s.zip, s.city].filter(Boolean).join(' ');
  return [s.address, line, s.countryId].filter(Boolean).join(', ') || '—';
};

/** Czy stacja ma oba krańce okna — brak jednego znaczy termin nieustalony. */
const hasFullTimeWindow = (s: MessageStation) =>
  !!(s.fromDate && s.fromTime && s.untilDate && s.untilTime);

export const MessageContentPanel = () => {
  const record = useRecordContext<RaRecord>();
  const notify = useNotify();
  const [content, setContent] = useState<MessageContent | null>(null);
  const [busy, setBusy] = useState(false);

  const id = record?.id;

  useEffect(() => {
    if (id === undefined) return;
    let alive = true;
    setBusy(true);
    fetchMessageContent(id)
      .then((w) => {
        if (alive) setContent(w);
      })
      .catch((e) => {
        // Awaria odczytu nie może zabrać całego ekranu zlecenia — nagłówek
        // i tak jest przydatny.
        if (alive) notify(toErrorMessage(e), { type: 'error' });
      })
      .finally(() => {
        if (alive) setBusy(false);
      });
    return () => {
      alive = false;
    };
  }, [id, notify]);

  if (!record) return null;

  return (
    <SectionCard title="Zawartość komunikatu">
      {busy && content === null && <CircularProgress size={20} />}

      {content && content.ostrzezenia.length > 0 && (
        <Alert severity="warning" sx={{ mb: 1 }}>
          <AlertTitle>W komunikacie brakuje danych</AlertTitle>
          <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
            {content.ostrzezenia.map((o) => (
              <li key={o}>
                <Typography variant="body2">{o}</Typography>
              </li>
            ))}
          </Box>
        </Alert>
      )}

      {content && content.quantities.length > 0 && (
        <Stack
          direction="row"
          spacing={0.5}
          sx={{ mb: 1, flexWrap: 'wrap' }}
          useFlexGap
        >
          <Typography variant="caption" color="text.secondary">
            Razem:
          </Typography>
          {content.quantities.map((i) => (
            <Chip
              key={`${i.qualifier}|${i.unit ?? ''}`}
              size="small"
              variant="outlined"
              label={
                `${i.qualifier} ${i.total}` +
                (i.unit ? ` ${i.unit}` : '') +
                (i.unparsed > 0
                  ? ` (+${i.unparsed} nieodczytane)`
                  : '')
              }
            />
          ))}
        </Stack>
      )}

      {content && content.stacje.length > 0 && (
        <Box sx={{ overflowX: 'auto', mb: 2 }}>
          <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
            Stacje ({content.stacje.length})
          </Typography>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>#</TableCell>
                <TableCell>Typ</TableCell>
                <TableCell>Firma</TableCell>
                <TableCell>Adres</TableCell>
                <TableCell>Okno od</TableCell>
                <TableCell>Okno do</TableCell>
                <TableCell>Przesyłka</TableCell>
                <TableCell>Uwagi</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {content.stacje.map((s) => (
                <TableRow key={`${s.sequence}-${s.id ?? ''}`} hover>
                  <TableCell>{s.sequence}</TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      variant="outlined"
                      label={STATION_TYPE_LABELS[s.type ?? ''] ?? s.type ?? '?'}
                    />
                  </TableCell>
                  <TableCell>{s.companyName ?? '—'}</TableCell>
                  <TableCell>{formatAddress(s)}</TableCell>
                  <TableCell
                    sx={{ color: hasFullTimeWindow(s) ? undefined : 'warning.main' }}
                  >
                    {formatTimeWindow(s.fromDate, s.fromTime)}
                  </TableCell>
                  <TableCell
                    sx={{ color: hasFullTimeWindow(s) ? undefined : 'warning.main' }}
                  >
                    {formatTimeWindow(s.untilDate, s.untilTime)}
                  </TableCell>
                  <TableCell>{s.shipment ?? '—'}</TableCell>
                  <TableCell>
                    {[s.avis, s.comment].filter(Boolean).join(' · ') || '—'}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      )}

      {content?.shipments.map((p) => (
        <Box key={`${p.id ?? ''}-${p.number ?? ''}`} sx={{ mb: 2 }}>
          <Stack
            direction="row"
            spacing={1}
            sx={{ alignItems: 'center', flexWrap: 'wrap', mb: 0.5 }}
            useFlexGap
          >
            <Typography variant="subtitle2">
              Przesyłka {p.number ?? p.id ?? '—'}
            </Typography>
            {p.customerName && (
              <Chip size="small" variant="outlined" label={p.customerName} />
            )}
            {p.weight !== null && (
              <Chip
                size="small"
                variant="outlined"
                label={`${p.weight} ${p.weightUnit ?? ''}`.trim()}
              />
            )}
          </Stack>
          {p.items.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              Przesyłka bez pozycji.
            </Typography>
          ) : (
            <Box sx={{ overflowX: 'auto' }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Poz.</TableCell>
                    <TableCell>Opis</TableCell>
                    <TableCell>Nr materiału</TableCell>
                    <TableCell>Ilości</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {p.items.map((it, i) => (
                    <TableRow key={`${it.id ?? ''}-${i}`} hover>
                      <TableCell>{it.posIndex ?? i + 1}</TableCell>
                      <TableCell>
                        {it.description ?? it.shortDescription ?? '—'}
                      </TableCell>
                      <TableCell>{it.materialNumber ?? '—'}</TableCell>
                      <TableCell>
                        {it.quantities.length === 0
                          ? '—'
                          : it.quantities
                              .map((q) =>
                                [q.qualifier, q.value, q.unit]
                                  .filter(Boolean)
                                  .join(' '),
                              )
                              .join(' · ')}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          )}
        </Box>
      ))}

      {content && content.stacje.length === 0 && content.shipments.length === 0 && (
        <Typography variant="body2" color="text.secondary">
          Zapisany komunikat nie zawiera stacji ani przesyłek — poniżej surowe
          poddrzewo, jeśli trzeba sprawdzić, co w nim jest.
        </Typography>
      )}
    </SectionCard>
  );
};
